import React, { useState, createContext, useEffect, useRef } from "react"
import { getDeviceInfo } from "~/utils/functions"
import {
  DEFAULT_VIDEO_CONSTRAINTS,
  SELECTED_AUDIO_INPUT_KEY,
  SELECTED_VIDEO_INPUT_KEY,
  TRACK_READY_STATE,
  PERMISSION_STATE,
} from "~/constants/twilio"
import useDevices from "~/composable/twilio/useDevices"
import { usePermissionState } from "~/composable/twilio/usePermissionState"
import { isMobile } from "~/utils/functions"

type LocalTracksContextType = {
  videoStreamRef: React.RefObject<MediaStream | null>
  audioStreamRef: React.RefObject<MediaStream | null>
  videoId: string | null
  audioId: string | null
  toggleAudio: () => Promise<string | null>
  toggleVideo: () => Promise<string | null>
  videoRef: React.RefObject<HTMLVideoElement>
  audioRef: React.RefObject<HTMLAudioElement>
  videoEnabled: boolean
  audioEnabled: boolean
  hasVideo: boolean
  hasAudio: boolean
  localTrackLeaveSession: () => void
}

export const LocalTracksContext = createContext<LocalTracksContextType | null>(null)

interface LocalTracksProviderProps {
  children: React.ReactNode
  initEnableAudio?: boolean
}

let handleEventOnEndedVideo
let handleEventOnEndedAudio
let handleEventOnMuteAudio
let handleEventOnUnMuteAudio

export function LocalTracksProvider({
  children,
  initEnableAudio = true,
}: LocalTracksProviderProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const videoStreamRef = useRef<MediaStream | null>(null)
  const audioStreamRef = useRef<MediaStream | null>(null)
  const [videoId, setVideoId] = useState<string | null>(null)
  const [audioId, setAudioId] = useState<string | null>(null)
  const [videoEnabled, setVideoEnabled] = useState(false)
  const [audioEnabled, setAudioEnabled] = useState(false)
  const [hasVideo, setHasVideo] = useState(false)
  const [hasAudio, setHasAudio] = useState(false)
  // HungHV: have device or not
  const { hasVideoInputDevices, hasAudioInputDevices } = useDevices()
  // HungHV: have permission to use the device
  const cameraPermission = usePermissionState("camera" as PermissionName)
  const microphonePermission = usePermissionState("microphone" as PermissionName)

  const getCameraDevices = async (): Promise<string | null> => {
    const { videoInputDevices, hasVideoInputDevices } = await getDeviceInfo()
    const savedVideoId = localStorage.getItem(SELECTED_VIDEO_INPUT_KEY)

    const selectedVideo =
      videoInputDevices.find((c) => c.deviceId === savedVideoId) || videoInputDevices[0]

    if (selectedVideo) localStorage.setItem(SELECTED_VIDEO_INPUT_KEY, selectedVideo.deviceId)
    setHasVideo(hasVideoInputDevices)
    setVideoId(selectedVideo?.deviceId)
    return selectedVideo?.deviceId
  }

  const getAudioDevices = async (): Promise<string | null> => {
    const { audioInputDevices, hasAudioInputDevices } = await getDeviceInfo()
    const savedAudioId = localStorage.getItem(SELECTED_AUDIO_INPUT_KEY)

    const selectedAudio =
      audioInputDevices.find((m) => m.deviceId === savedAudioId) || audioInputDevices[0]

    if (selectedAudio) localStorage.setItem(SELECTED_AUDIO_INPUT_KEY, selectedAudio.deviceId)
    setHasAudio(hasAudioInputDevices)
    setAudioId(selectedAudio?.deviceId)
    return selectedAudio?.deviceId
  }

  const getPreferredMediaTracks = async (): Promise<void> => {
    const {
      audioInputDevices,
      videoInputDevices,
      hasAudioInputDevices,
      hasVideoInputDevices,
    } = await getDeviceInfo()
    const cameraPermission = await navigator.permissions.query({ name: "camera" as PermissionName })

    if (hasVideoInputDevices && cameraPermission.state === PERMISSION_STATE.GRANTED) {
      // Video stream processing
      try {
        videoStreamRef.current = await navigator.mediaDevices.getUserMedia({
          video: hasVideoInputDevices && DEFAULT_VIDEO_CONSTRAINTS,
        })

        // Video track processing
        const videoTrack = videoStreamRef.current?.getVideoTracks()[0]
        if (videoTrack) {
          setVideoEnabled(videoTrack.enabled)
          handleEventOnEndedVideo = () => {
            setVideoEnabled(false)
            if (videoRef.current) {
              videoRef.current.srcObject = null
            }
          }
          videoTrack.addEventListener("ended", handleEventOnEndedVideo)
        }
      } catch (error) {
        console.log("Error accessing video stream:", error)
      }
    }

    const microphonePermission = await navigator.permissions.query({
      name: "microphone" as PermissionName,
    })

    if (hasAudioInputDevices && microphonePermission.state === PERMISSION_STATE.GRANTED) {
      // Audio stream processing
      try {
        audioStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: hasAudioInputDevices && {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
        })

        // Audio track processing
        const audioTrack = audioStreamRef.current?.getAudioTracks()[0]
        if (audioTrack) {
          audioTrack.enabled = initEnableAudio
          setAudioEnabled(audioTrack.enabled)
          handleEventOnEndedAudio = () => setAudioEnabled(false)
          handleEventOnMuteAudio = () => setAudioEnabled(false)
          handleEventOnUnMuteAudio = () => setAudioEnabled(true)
          audioTrack.addEventListener("ended", handleEventOnEndedAudio)
          audioTrack.addEventListener("mute", handleEventOnMuteAudio)
          audioTrack.addEventListener("unmute", handleEventOnUnMuteAudio)
        }
      } catch (error) {
        console.log("Error accessing audio stream:", error)
      }
    }

    const savedVideoId = localStorage.getItem(SELECTED_VIDEO_INPUT_KEY)
    const savedAudioId = localStorage.getItem(SELECTED_AUDIO_INPUT_KEY)

    const selectedVideo =
      videoInputDevices.find((c) => c.deviceId === savedVideoId) || videoInputDevices[0]
    const selectedAudio =
      audioInputDevices.find((m) => m.deviceId === savedAudioId) || audioInputDevices[0]

    if (selectedVideo) localStorage.setItem(SELECTED_VIDEO_INPUT_KEY, selectedVideo.deviceId)
    if (selectedAudio) localStorage.setItem(SELECTED_AUDIO_INPUT_KEY, selectedAudio.deviceId)
    setHasVideo(hasVideoInputDevices)
    setHasAudio(hasAudioInputDevices)
    setVideoId(selectedVideo?.deviceId)
    setAudioId(selectedAudio?.deviceId)
  }

  const toggleVideo = async (): Promise<string | null> => {
    const videoTrack = videoStreamRef.current?.getVideoTracks()[0]
    // HungHV: When track ended => need to re-initialize stream and strack
    if (!videoTrack || videoTrack?.readyState !== TRACK_READY_STATE.LIVE) {
      let stream = await navigator.mediaDevices.getUserMedia({
        video: DEFAULT_VIDEO_CONSTRAINTS,
      })
      let videoId: string | null = await getCameraDevices()

      if (isMobile) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: DEFAULT_VIDEO_CONSTRAINTS,
        })
      }

      videoStreamRef.current = stream
      const newTrack = stream.getVideoTracks()[0]
      newTrack.enabled = true
      newTrack.addEventListener("ended", handleEventOnEndedVideo)
      setVideoEnabled(true)

      return videoId
    }

    videoTrack.enabled = !videoTrack.enabled
    if (!videoTrack.enabled) {
      videoTrack.removeEventListener("ended", handleEventOnEndedVideo)
      videoTrack.stop()
      setVideoEnabled(false)
    } else {
      setVideoEnabled(true)
    }
    return videoId
  }

  const toggleAudio = async (): Promise<string | null> => {
    const audioTrack = audioStreamRef.current?.getAudioTracks()[0]
    // HungHV: When track ended => need to re-initialize stream and strack
    if (!audioTrack || audioTrack.readyState !== TRACK_READY_STATE.LIVE) {
      let stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      })
      let audioId: string | null

      if (!audioId) {
        audioId = await getAudioDevices()
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
        })
      }

      audioStreamRef.current = stream
      const newTrack = stream.getAudioTracks()[0]
      newTrack.enabled = true
      setAudioEnabled(true)
      return audioId
    }
    audioTrack.enabled = !audioTrack.enabled
    setAudioEnabled(audioTrack.enabled)
    return audioId
  }

  const localTrackLeaveSession = () => {
    videoStreamRef.current?.getTracks().forEach((track) => {
      track.stop()
      track.removeEventListener("ended", handleEventOnEndedVideo)
    })
    audioStreamRef.current?.getTracks().forEach((track) => {
      track.stop()
      track.removeEventListener("ended", handleEventOnEndedAudio)
      track.removeEventListener("mute", handleEventOnMuteAudio)
      track.removeEventListener("unmute", handleEventOnUnMuteAudio)
    })
  }

  useEffect(() => {
    if (!hasVideoInputDevices || cameraPermission === PERMISSION_STATE.DENIED) {
      setVideoEnabled(false)
    }
    if (!hasAudioInputDevices || microphonePermission === PERMISSION_STATE.DENIED) {
      setAudioEnabled(false)
    }
  }, [cameraPermission, hasVideoInputDevices, microphonePermission, hasAudioInputDevices])

  useEffect(() => {
    getPreferredMediaTracks()

    window.addEventListener("beforeunload", localTrackLeaveSession)
    window.addEventListener("popstate", localTrackLeaveSession)

    return () => {
      localTrackLeaveSession()
    }
  }, [])

  return (
    <LocalTracksContext.Provider
      value={{
        videoStreamRef,
        audioStreamRef,
        videoId,
        audioId,
        toggleAudio,
        toggleVideo,
        videoRef,
        audioRef,
        videoEnabled,
        audioEnabled,
        hasVideo,
        hasAudio,
        localTrackLeaveSession,
      }}
    >
      {children}
    </LocalTracksContext.Provider>
  )
}
